import React, { useEffect, useState, useCallback } from 'react';

interface XpToastItem {
  id: number;
  xp: number;
  leveledUp: boolean;
  newLevel?: number;
}

let toastId = 0;

/**
 * XpToast — floating "+X XP" animation that appears when learners earn XP.
 * Usage: call showXp(xpEarned, leveledUp, newLevel) from any learning page.
 */
export function useXpToast() {
  const [toasts, setToasts] = useState<XpToastItem[]>([]);

  const showXp = useCallback((xp: number, leveledUp = false, newLevel?: number) => {
    if (xp <= 0) return;
    const id = ++toastId;
    setToasts(prev => [...prev, { id, xp, leveledUp, newLevel }]);

    // Auto-remove after animation
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 2200);
  }, []);

  return { toasts, showXp };
}

interface XpToastContainerProps {
  toasts: XpToastItem[];
}

const XpToastContainer: React.FC<XpToastContainerProps> = ({ toasts }) => {
  if (toasts.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 80,
        right: 24,
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        pointerEvents: 'none',
      }}
    >
      {toasts.map(toast => (
        <XpToastBubble key={toast.id} toast={toast} />
      ))}
      <style>{`
        @keyframes xpFloat {
          0% { opacity: 0; transform: translateY(20px) scale(0.8); }
          15% { opacity: 1; transform: translateY(0) scale(1.1); }
          30% { transform: translateY(0) scale(1); }
          80% { opacity: 1; transform: translateY(-10px); }
          100% { opacity: 0; transform: translateY(-30px); }
        }
        @keyframes levelUpPulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.15); }
          100% { transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

const XpToastBubble: React.FC<{ toast: XpToastItem }> = ({ toast }) => {
  return (
    <div
      style={{
        animation: 'xpFloat 2s ease-out forwards',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
      }}
    >
      <div
        style={{
          background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
          color: '#78350f',
          fontWeight: 700,
          fontSize: 18,
          padding: '6px 16px',
          borderRadius: 20,
          boxShadow: '0 4px 12px rgba(245, 158, 11, 0.4)',
          whiteSpace: 'nowrap',
        }}
      >
        +{toast.xp} XP
      </div>
      {toast.leveledUp && toast.newLevel && (
        <div
          style={{
            animation: 'levelUpPulse 0.6s ease-in-out 3',
            background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
            color: '#fff',
            fontWeight: 700,
            fontSize: 14,
            padding: '4px 12px',
            borderRadius: 16,
            boxShadow: '0 4px 12px rgba(109, 40, 217, 0.4)',
            whiteSpace: 'nowrap',
          }}
        >
          Level Up! Lv.{toast.newLevel}
        </div>
      )}
    </div>
  );
};

export default XpToastContainer;
